// ProductGrid.tsx

import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import Pagination from './Pagination';
// 🔥 IMPORT SHARED TYPES
import { MultiFilterMutation } from '../../../../types/Multifilter.types';

interface ProductGridProps {
  productItemCount: number;
  getProductsMutation: MultiFilterMutation; // 🔥 USE SHARED TYPE
  totalPages: number;
  currentPage: number;
  previousPage: () => void;
  nextPage: () => void;
  lastPage: () => void;
  firstPage: () => void;
  anchorRef: React.RefObject<HTMLDivElement>;
}

const ProductGrid = ({
                       productItemCount,
                       getProductsMutation,
                       totalPages,
                       currentPage,
                       previousPage,
                       nextPage,
                       lastPage,
                       firstPage,
                       anchorRef,
                     }: ProductGridProps) => {
  const navigate = useNavigate();

  return (
    <>
      <Box>
        <Pagination
          totalPages={totalPages}
          currentPage={currentPage}
          previousPage={previousPage}
          nextPage={nextPage}
          productItemCount={productItemCount}
          lastPage={lastPage}
          firstPage={firstPage}
        />
      </Box>
      <Grid container spacing={2} pl={0} pr={0}>
        {getProductsMutation.isLoading ? (
          <Box
            sx={{
              height: '50rem',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : getProductsMutation.isError ? (
          <>
            <Typography>An error occurred, please try a different filter</Typography>
          </>
        ) : (
          <>
            {getProductsMutation.data?.products
              ?.sort((a, b) =>
                (a.design || '').toLowerCase()
                  .localeCompare((b.design || '').toLowerCase())
              )
              .map((product, index) => (
                <Grid
                  item
                  xs={6}
                  sm={4}
                  key={index}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: {
                          xs: '150px',
                          sm: '300px',
                        },
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        // Format size string based on available data
                        const sizeString = product.diameter && product.diameter !== '0cm'
                          ? product.diameter.trim()
                          : `${product.width?.trim()}x${product.length_cm?.trim()}`;

                        // 🔥 UPDATED: Remove application from URL to match trade structure
                        navigate(
                          `../item_group/${product.item_group}/collection/${encodeURIComponent(
                            product.range
                          )}/design/${encodeURIComponent(
                            product.design
                          )}/colour/${encodeURIComponent(product.colour)}?targetSize=${encodeURIComponent(sizeString)}&targetProduct=${encodeURIComponent(product.sku)}`
                        );
                      }}
                    >
                      <img
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                        src={product.primaryImageUrl}
                        alt={`${product.design} in ${product.colour}`}
                      />
                    </Box>
                    <Typography
                      fontWeight="600"
                      fontSize="18px"
                      lineHeight={'33px'}
                      textTransform={'capitalize'}
                      mt={1}
                      mb={0}
                    >
                      {product.design}
                    </Typography>
                    <Typography fontWeight="400" fontSize="16px">
                      {product.colour}
                    </Typography>

                    {product.web_size !== '-' && (
                      <Typography
                        fontWeight="400"
                        fontSize="14px"
                        mb={1}
                        fontStyle="italic"
                      >
                        {product.web_size}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              ))}
          </>
        )}
      </Grid>
      <Pagination
        totalPages={totalPages}
        currentPage={currentPage}
        previousPage={previousPage}
        nextPage={nextPage}
        productItemCount={productItemCount}
        lastPage={lastPage}
        firstPage={firstPage}
      />
    </>
  );
};


export default ProductGrid;